import React, { FC, useEffect, useState } from 'react';
import { Field, reduxForm } from 'redux-form';
import { checkBox, newThemeInput } from '_fields/inputs';
import { lowerCase, words } from '_fields/normalizers';
import { connect } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { validateUserByEmail } from '../../../api/validateUserByEmail';
import { formValueSelector } from 'redux-form';
import { useForm } from 'react-hook-form';
import { Form } from '@components/shadcn/components/ui/form';
import { RootState } from '@store/reducers';
import { Spinner } from '@components/Spinner/Spinner';
import { Helmet } from 'react-helmet';
import { Text, useIsInIframe } from 'shared-components';
import { ApplyWizardSteps, STEP_INTEGRATED_LOGIN, STEP_INFO } from '@components/ApplyWizard/ApplyWizardSteps';
import { reduxFormZodValidate } from '@utils/reduxFormZodValidate';
import { Auth0LoggedInSetState } from '../../../Auth0LoggedInSetState';
import { useApplyWizardContext } from '../../../context/ApplyWizardContext';
import { change } from 'redux-form';
import { Dispatch } from 'redux';
import { BCMShortWizardShell, BCMShortSubmitButton } from '@components/BCMShort/WizardShell';

type EnhancedInfoProps = {
  dispatch: Dispatch;
  change: any;
  partnerCustomFields: any;
  vendor_url: string;
  ownerEmail: string;
  loanType: string;
  saving: boolean;
  handleSubmit: () => void;
  invalid: boolean;
  user: RootState['user'];
  owner_1_first?: string;
  owner_1_last?: string;
  owner_1_mobile?: string;
};

const EnhancedInfo: FC<EnhancedInfoProps> = (props) => {
  const auth0 = useAuth0();

  if (auth0.isLoading) {
    return (
      <BCMShortWizardShell currentStepIndex={0}>
        <div className="flex items-center justify-center w-full py-[60px]">
          <Spinner />
        </div>
      </BCMShortWizardShell>
    );
  }

  return <EnhancedInfoInner {...props} />;
};
const EnhancedInfoInner: FC<EnhancedInfoProps> = ({
  dispatch,
  change,
  handleSubmit,
  invalid,
  saving,
  partnerCustomFields,
  vendor_url,
  ownerEmail,
  loanType,
  user,
  owner_1_first,
  owner_1_last,
  owner_1_mobile,
}) => {
  const auth0 = useAuth0();
  const isOpenedInIframe = useIsInIframe();

  const form = useForm();
  const [{ data: validateUserResponse, loading: validateUserLoading, error: validateUserError }, validateUser] =
    validateUserByEmail();
  const { currentPath, applicationFlow, changeStepTo } = useApplyWizardContext();
  const privacyLink = `${vendor_url}/privacy-policy`;
  const termsLink = `${vendor_url}/terms`;
  const [error, setError] = useState(false);

  useEffect(() => {
    if (validateUserResponse) {
      if (validateUserResponse.userWithThisEmailExists === false) {
        if (changeStepTo && isOpenedInIframe) {
          changeStepTo(ApplyWizardSteps[STEP_INTEGRATED_LOGIN].id);
        } else {
          handleSubmit();
        }
      } else if (validateUserResponse.userWithThisEmailExists === true) {
        setError(true);
      }
    }
  }, [validateUserResponse]);
  useEffect(() => {
    dispatch(change('application', 'application_type', 'bcm-short'));
  }, []);

  console.log('applicationFlow', applicationFlow, 'has changeStepTo?', !!changeStepTo);
  return (
    <BCMShortWizardShell currentStepIndex={0}>
      <Helmet>
        <title>Basic Info - Your Loan Application</title>
      </Helmet>
      <Auth0LoggedInSetState />

      <div className="flex items-center justify-center py-[20px] w-full">
        <Text variant="headline-sm" className="!font-extended !uppercase !text-[16px] !leading-none text-center">
          Let&apos;s Begin
        </Text>
      </div>

      <div className="flex flex-col gap-[10px] items-center w-full">
        <div className="flex items-center justify-center py-[10px] w-full">
          <Text variant="body-general" className="!text-[20px] !leading-[30px] text-center">
            Your Information
          </Text>
        </div>

        <Form {...form}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setError(false);
              if (auth0.isAuthenticated && user.email) {
                handleSubmit();
              } else {
                validateUser({ data: { email: ownerEmail } });
              }
            }}
            className="w-full"
          >
            <div className="w-full flex flex-col gap-[10px]">
              <div className="w-full max-w-[368px] mx-auto">
                <span className="font-condensed text-[14px] text-charcoal leading-[1.2] block text-left pb-[6px] w-full">
                  BUSINESS NAME*
                </span>
                <Field
                  autoFocus
                  name="business_name"
                  component={newThemeInput}
                  type="text"
                  placeholder="Business Name*"
                  normalize={words}
                  label="Business Name*"
                  data-testid="business_name"
                  className="!bg-dawn3"
                />
              </div>

              <div className="w-full max-w-[368px] mx-auto">
                <span className="font-condensed text-[14px] text-charcoal leading-[1.2] block text-left pb-[6px] w-full">
                  FIRST NAME*
                </span>
                <Field
                  name="owner_1_first"
                  component={newThemeInput}
                  type="text"
                  label="First Name*"
                  placeholder="First Name*"
                  normalize={words}
                  data-testid="owner_1_first"
                  className="!bg-dawn3"
                />
              </div>

              <div className="w-full max-w-[368px] mx-auto">
                <span className="font-condensed text-[14px] text-charcoal leading-[1.2] block text-left pb-[6px] w-full">
                  LAST NAME*
                </span>
                <Field
                  name="owner_1_last"
                  component={newThemeInput}
                  type="text"
                  label="Last Name*"
                  placeholder="Last Name*"
                  normalize={words}
                  data-testid="owner_1_last"
                  className="!bg-dawn3"
                />
              </div>

              <div className="w-full max-w-[368px] mx-auto">
                <span className="font-condensed text-[14px] text-charcoal leading-[1.2] block text-left pb-[6px] w-full">
                  {auth0.isAuthenticated && user.email ? 'EMAIL (DISABLED)' : 'EMAIL*'}
                </span>
                <Field
                  name="owner_1_email"
                  component={newThemeInput}
                  type="email"
                  label={auth0.isAuthenticated && user.email ? 'Email (disabled)' : 'Email*'}
                  placeholder="Email*"
                  normalize={lowerCase}
                  data-testid="owner_1_email"
                  disabled={!!(auth0.isAuthenticated && user.email)}
                  className="!bg-dawn3"
                />
              </div>

              <div className="w-full max-w-[368px] mx-auto">
                <span className="font-condensed text-[14px] text-charcoal leading-[1.2] block text-left pb-[6px] w-full">
                  MOBILE PHONE*
                </span>
                <Field
                  name="owner_1_mobile"
                  component={newThemeInput}
                  label="Mobile Phone*"
                  placeholder="Mobile Phone*"
                  formatAsPhone
                  data-testid="owner_1_mobile"
                  className="!bg-dawn3"
                />
              </div>

              {error && (
                <div className="w-full">
                  <Text variant="error" className="!text-[14px]">
                    This email is already registered with us. Please{' '}
                    <button
                      type="button"
                      onClick={() => {
                        if (changeStepTo && isOpenedInIframe) {
                          changeStepTo(ApplyWizardSteps[STEP_INTEGRATED_LOGIN].id);
                        } else {
                          auth0.loginWithRedirect({
                            appState: {
                              returnTo: '/app/verify/login',
                            },
                            authorizationParams: {
                              connection: 'email',
                              screen_hint: 'signup',
                              login_hint: ownerEmail,
                              ...(owner_1_first ? { 'user[given_name]': owner_1_first } : {}),
                              ...(owner_1_last ? { 'user[family_name]': owner_1_last } : {}),
                              ...(owner_1_mobile ? { 'user[phone_number]': owner_1_mobile } : {}),
                            },
                          });
                        }
                      }}
                      className="underline text-moss cursor-pointer bg-transparent border-0 p-0"
                    >
                      login
                    </button>
                  </Text>
                </div>
              )}

              {/* Wrap in flex+justify-center so the 368px-capped
                  .tento-form-element Stack is centered like the inputs
                  above (which get centered via grid `justify-items-center`
                  in newThemeInput's FormItem). */}
              <div className="w-full pt-[10px] flex justify-center">
                <Field
                  component={checkBox}
                  id="consent"
                  name="consent"
                  variant="transparent-black"
                  stackProps={{ className: 'w-full !my-0' }}
                  labelProps={{
                    className: '!font-standard !text-[14px] !leading-[1.4] !text-charcoal self-center',
                  }}
                  data-testid="consent_check"
                  children={
                    <label htmlFor="consent">
                      I agree to the{' '}
                      <a target="_blank" href={termsLink} className="!no-underline !text-moss" tabIndex={-1}>
                        terms and conditions
                      </a>{' '}
                      and{' '}
                      <a target="_blank" href={privacyLink} className="!no-underline !text-moss" tabIndex={-1}>
                        privacy policy
                      </a>
                      .
                    </label>
                  }
                />
              </div>

              <div className="flex flex-col items-center justify-center w-full pt-[20px] pb-[10px] px-[10px] gap-[10px]">
                <BCMShortSubmitButton
                  invalid={invalid}
                  saving={saving || validateUserLoading}
                  data-testid="info_continue"
                >
                  Continue
                </BCMShortSubmitButton>
                <Text variant="body-form-label" className="!text-[14px] !leading-[18px] !font-condensed">
                  *No hard checks to your credit
                </Text>
              </div>
            </div>
          </form>
        </Form>
      </div>
    </BCMShortWizardShell>
  );
};

const Info = reduxForm<EnhancedInfoProps, EnhancedInfoProps>({
  form: 'application',
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
  validate: reduxFormZodValidate(ApplyWizardSteps[STEP_INFO].zodSchema),
})(EnhancedInfo);
const appSelector = formValueSelector('application');

const mapStateToProps = (state: RootState) => {
  return {
    partnerCustomFields: state.partnerCustomFields,
    vendor_url: state.brand?.data?.vendor_url,
    ownerEmail: appSelector(state, 'owner_1_email'),
    user: state.user,
    owner_1_first: appSelector(state, 'owner_1_first'),
    owner_1_last: appSelector(state, 'owner_1_last'),
    owner_1_mobile: appSelector(state, 'owner_1_mobile'),
  };
};

export const BCMInfo2 = connect(mapStateToProps, (dispatch) => ({
  change,
  dispatch,
}))(Info);
